import { useState, useEffect, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Button,
  CircularProgress,
  Chip,
  IconButton,
  Skeleton,
  TextField,
  InputAdornment,
  Alert,
  Fade,
  Dialog,
  Snackbar,
  useMediaQuery,
  Tabs,
  Tab,
  Avatar,
} from '@mui/material'
import { styled, createTheme, ThemeProvider, alpha } from '@mui/material/styles'
import {
  Article,
  CalendarToday,
  ArrowForward,
  Search,
  Clear,
  AccessTime,
  Visibility,
} from '@mui/icons-material'
import apiService from '../services/ApiService'
import {StaticPageWrapper} from './RouteWrappers'
import { parseHtmlSanitizeAddTargetToLinks } from '../services/utils'
import { generateBlogListingSchema } from '../services/schemaGenerators'
import { getCountryFromDomain } from '../utils/seoConfig'
import { useAuth } from '../context/AuthContext'
// Theme (keeping your existing theme)
const theme = createTheme({
  palette: {
    primary: {
      main: '#ffda1b',
      contrastText: '#23252a',
    },
    secondary: {
      main: '#23252a',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f9fafb',
      paper: '#ffffff',
    },
    text: {
      primary: '#1e293b',
      secondary: '#64748b',
    },
  },
  typography: {
    fontFamily: 'Inter, sans-serif',
    h1: {
      fontSize: 'clamp(1.75rem, 4vw, 2.75rem)', // Responsive scaling
      fontWeight: 700,
      letterSpacing: '-0.025em',
      lineHeight: { xs: 1.2, md: 1.1 },
    },
    h2: {
      fontSize: 'clamp(1.5rem, 3vw, 2rem)',
      fontWeight: 600,
      letterSpacing: '-0.02em',
      lineHeight: { xs: 1.3, md: 1.2 },
    },
    h3: {
      fontSize: 'clamp(1.25rem, 2.5vw, 1.5rem)',
      fontWeight: 600,
      fontFamily: 'Montserrat, sans-serif',
    },
    h4: {
      fontSize: 'clamp(1rem, 2vw, 1.25rem)',
      fontWeight: 600,
    },
    body1: {
      fontSize: 'clamp(0.875rem, 1.5vw, 1rem)',
      lineHeight: 1.6,
      fontFamily: 'Montserrat, sans-serif',
    },
    body2: {
      fontSize: 'clamp(0.75rem, 1.2vw, 0.875rem)',
      lineHeight: 1.5,
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  shape: {
    borderRadius: 8,
  },
})

// Styled components (keeping your existing styles)
const HeroSection = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, #23252a 0%, #374151 100%)`,
  color: 'white',
  padding: theme.spacing(4, 0), // Reduced base padding
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(6, 0),
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(8, 0),
  },
  [theme.breakpoints.up('lg')]: {
    padding: theme.spacing(10, 0),
  },
}))

const GoldDivider = styled(Box)(() => ({
  width: '64px',
  height: '3px',
  backgroundColor: '#ffda1b',
  margin: '0 auto 24px auto',
}))
const BlogCard = styled(Card)(({ theme }) => ({
  height: '100%',
  width: '100%',
  borderRadius: theme.spacing(1),
  border: '1px solid #e2e8f0',
  backgroundColor: '#ffffff',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  position: 'relative',
  overflow: 'hidden',
  display: 'flex', // Add this
  flexDirection: 'column', // Add this

  [theme.breakpoints.up('sm')]: {
    borderRadius: theme.spacing(1.5),
  },

  '&:hover': {
    transform: { xs: 'none', sm: 'translateY(-6px)' },
    boxShadow: {
      xs: '0 4px 12px rgba(0, 0, 0, 0.1)',
      sm: '0 20px 40px -12px rgba(0, 0, 0, 0.15), 0 8px 16px -8px rgba(0, 0, 0, 0.1)',
    },
    borderColor: 'rgba(255, 218, 27, 0.3)',
    '& .blog-image': {
      transform: { xs: 'none', sm: 'scale(1.05)' },
    },
    '& .blog-title': {
      color: '#23252a',
    },
  },
}))

const FilterSection = styled(Box)(({ theme }) => ({
  backgroundColor: 'white',
  borderBottom: '1px solid #e2e8f0',
  padding: theme.spacing(2, 0), // Reduced padding on mobile

  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(2.5, 0),
  },

  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(3, 0),
  },
}))

const CategoryChip = styled(Chip)(({ theme, selected }) => ({
  margin: theme.spacing(0.25), // Tighter spacing on mobile
  fontWeight: 600,
  fontSize: '0.75rem',
  height: 28, // Smaller height on mobile

  [theme.breakpoints.up('sm')]: {
    margin: theme.spacing(0.5),
    fontSize: '0.8125rem',
    height: 32,
  },

  ...(selected
    ? {
        backgroundColor: '#23252a',
        color: 'white',
        '&:hover': {
          backgroundColor: '#1a1c21',
        },
      }
    : {
        backgroundColor: '#f1f5f9',
        color: '#475569',
        '&:hover': {
          backgroundColor: '#e2e8f0',
        },
      }),
}))

const TEAM_GTL_AVATAR_SRC = '/favicon-96x96.png'

const getProxiedUrl = (originalUrl) => {
  if (!originalUrl || typeof originalUrl !== 'string') {
    return ''
  }

  const trimmed = originalUrl.trim()
  if (!trimmed) {
    return ''
  }

  if (trimmed.startsWith('/')) {
    return trimmed
  }

  const lowered = trimmed.toLowerCase()
  if (lowered.startsWith('data:') || lowered.startsWith('blob:')) {
    return trimmed
  }

  if (trimmed.includes('/api/v1/profile/avatar?url=')) {
    return trimmed
  }

  const backendBase =
    (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080').replace(
      /\/$/,
      '',
    )
  return `${backendBase}/api/v1/profile/avatar?url=${encodeURIComponent(trimmed)}`
}

const isTeamGtlAuthor = (authorType, authorName) => {
  const normalizedAuthorType = (authorType || '').toString().trim().toUpperCase()
  if (normalizedAuthorType === 'TEAM_GTL') {
    return true
  }
  const normalizedAuthorName = (authorName || '').toString().trim().toLowerCase()
  return normalizedAuthorName.includes('team gtl')
}

const isCommunityAuthor = (authorType) => {
  const normalizedAuthorType = (authorType || '').toString().trim().toUpperCase()
  return normalizedAuthorType === 'COMMUNITY' || normalizedAuthorType === 'USER'
}

const getAuthorDisplayName = (blog) => {
  const explicitName = (blog?.author || '').trim()
  if (explicitName) {
    return explicitName
  }
  return isTeamGtlAuthor(blog?.authorType, blog?.author) ? 'Team GTL' : 'Community'
}

const getAuthorAvatarSource = (blog) => {
  const possibleSources = [
    blog?.authorAvatarUrl,
    blog?.authorImageUrl,
    blog?.authorProfileImageUrl,
    blog?.authorPhotoUrl,
    blog?.profileImageUrl,
    blog?.userAvatarUrl,
    blog?.userProfileImageUrl,
    blog?.authorAvatar,
  ]
  const matched = possibleSources.find(
    (source) => typeof source === 'string' && source.trim()
  )
  if (matched) {
    return getProxiedUrl(matched)
  }
  return isTeamGtlAuthor(blog?.authorType, blog?.author) ? TEAM_GTL_AVATAR_SRC : ''
}

const getAuthorPublicProfilePath = (blog) => {
  if (!isCommunityAuthor(blog?.authorType)) {
    return ''
  }

  const usernameCandidates = [blog?.authorUsername, blog?.writerUsername, blog?.username]
  const matchedUsername = usernameCandidates.find(
    (value) => typeof value === 'string' && value.trim()
  )

  if (!matchedUsername) {
    return ''
  }

  const normalizedUsername = matchedUsername.trim().replace(/^@+/, '')
  if (!normalizedUsername) {
    return ''
  }

  return `/@${normalizedUsername}`
}

const getInitials = (value) => {
  const normalized = (value || '').trim()
  if (!normalized) {
    return 'U'
  }
  const parts = normalized.split(/\s+/).filter(Boolean)
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase()
  }
  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase()
}

const BLOG_SECTION_QUERY_KEY = 'section'

const normalizeSectionFromQuery = (search) => {
  const params = new URLSearchParams(search || '')
  const rawSection = (params.get(BLOG_SECTION_QUERY_KEY) || '')
    .toString()
    .trim()
    .toLowerCase()

  if (rawSection === 'community' || rawSection === 'user') {
    return 'COMMUNITY'
  }

  if (
    rawSection === 'team' ||
    rawSection === 'team_gtl' ||
    rawSection === 'team-gtl' ||
    rawSection === 'gtl'
  ) {
    return 'TEAM_GTL'
  }

  return 'TEAM_GTL'
}

const getSectionQueryValue = (sectionValue) =>
  sectionValue === 'COMMUNITY' ? 'community' : 'team'

const Blogs = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthenticated } = useAuth()
  const selectedSection = normalizeSectionFromQuery(location.search)
  const [blogs, setBlogs] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [categories, setCategories] = useState([])
  const [selectedCategory, setSelectedCategory] = useState('')
  const [error, setError] = useState(null)
  const [writerNotice, setWriterNotice] = useState('')
  const [writerNoticeOpen, setWriterNoticeOpen] = useState(false)

  // Fetch blogs from API with proper error handling
  const fetchBlogs = useCallback(
    async (
      pageNum = 1,
      isLoadMore = false,
      query = '',
      category = '',
      section = selectedSection
    ) => {
      if (isLoadMore) {
        setLoadingMore(true)
      } else {
        setLoading(true)
      }
      setError(null)

      try {
        let response

        if (query.trim()) {
          response = await apiService.searchBlogsBySection(
            query,
            pageNum,
            6,
            section
          )
        } else if (category) {
          response = await apiService.getBlogsByCategory(
            category,
            pageNum,
            6,
            null,
            section
          )
        } else {
          response = await apiService.getAllBlogs(pageNum, 6, section)
        }

        if (isLoadMore) {
          setBlogs((prevBlogs) => [...prevBlogs, ...response.content])
        } else {
          setBlogs(response.content)
        }

        setTotalPages(response.totalPages)
        setHasMore(pageNum < response.totalPages)
      } catch (error) {
        console.error('Error fetching blogs:', error)
        setError('Failed to load blogs. Please try again later.')

        if (!isLoadMore) {
          setBlogs([])
          setHasMore(false)
        }
      } finally {
        setLoading(false)
        setLoadingMore(false)
      }
    },
    [selectedSection]
  )

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const categoriesResponse = await apiService.getAllCategories()
      // Sort categories with "Others" at the end
      const sortedCategories = categoriesResponse.sort((a, b) => {
        if (a === 'Others') return 1
        if (b === 'Others') return -1
        return a.localeCompare(b)
      })
      setCategories(sortedCategories)
    } catch (error) {
      console.error('Error fetching categories:', error)
      setCategories([])
    }
  }

  // Initial load
  useEffect(() => {
    fetchCategories()
  }, [])

  useEffect(() => {
    const params = new URLSearchParams(location.search || '')
    if (params.has(BLOG_SECTION_QUERY_KEY)) {
      return
    }

    params.set(BLOG_SECTION_QUERY_KEY, getSectionQueryValue(selectedSection))
    navigate(
      {
        pathname: location.pathname,
        search: `?${params.toString()}`,
      },
      {
        replace: true,
        state: location.state,
      }
    )
  }, [location.pathname, location.search, location.state, navigate, selectedSection])

  useEffect(() => {
    const stateMessage = location.state?.blogWriterMessage
    const storageMessage = sessionStorage.getItem('blog_writer_notice')
    const message = stateMessage || storageMessage
    if (!message) {
      return
    }

    setWriterNotice(message)
    setWriterNoticeOpen(true)
    sessionStorage.removeItem('blog_writer_notice')

    if (stateMessage) {
      navigate(`${location.pathname}${location.search}`, {
        replace: true,
        state: null,
      })
    }
  }, [location.pathname, location.search, location.state, navigate])

  useEffect(() => {
    setPage(1)
    fetchBlogs(1, false, '', '', selectedSection)
  }, [selectedSection, fetchBlogs])

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      const nextPage = page + 1
      setPage(nextPage)
      fetchBlogs(nextPage, true, searchQuery, selectedCategory, selectedSection)
    }
  }

  const handleSearch = (e) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      setPage(1)
      setSelectedCategory('')
      fetchBlogs(1, false, searchQuery.trim(), '', selectedSection)
    }
  }

  const handleCategoryFilter = (category) => {
    const newCategory = selectedCategory === category ? '' : category
    setSelectedCategory(newCategory)
    setPage(1)
    setSearchQuery('')
    fetchBlogs(1, false, '', newCategory, selectedSection)
  }

  const handleSectionChange = (event, newValue) => {
    const params = new URLSearchParams(location.search || '')
    params.set(BLOG_SECTION_QUERY_KEY, getSectionQueryValue(newValue))
    navigate(
      {
        pathname: location.pathname,
        search: `?${params.toString()}`,
      },
      { replace: false }
    )
    setSearchQuery('')
    setSelectedCategory('')
  }

  // Navigate to individual blog page instead of opening dialog
  const handleBlogClick = (blogSlug) => {
    navigate(`/blogs/${blogSlug}`)
  }

  const handleStartWriting = () => {
    if (isAuthenticated) {
      navigate('/blogs/write')
      return
    }
    sessionStorage.setItem('redirectPath', '/blogs/write')
    navigate('/login')
  }

  const formatDate = (dateString) => {
    try {
      let date
      if (Array.isArray(dateString)) {
        const [year, month, day, hour = 0, minute = 0, second = 0] = dateString
        date = new Date(year, month - 1, day, hour, minute, second)
      } else {
        date = new Date(dateString)
      }
      if (isNaN(date.getTime())) {
        return 'Date unavailable'
      }
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    } catch (error) {
      return 'Date unavailable'
    }
  }

  const clearFilters = () => {
    setSearchQuery('')
    setSelectedCategory('')
    setPage(1)
    fetchBlogs(1, false, '', '', selectedSection)
  }
  const stripHtml = (html) => {
    if (!html) return ''
    if (typeof html === 'object') return JSON.stringify(html)

    const div = document.createElement('div')
    div.innerHTML = html
    return div.textContent || div.innerText || ''
  }
  // Enhanced excerpt generation
  const generateExcerpt = (blog) => {
    // Return raw HTML content for parsing
    if (blog.excerpt) return blog.excerpt
    if (blog.description) return blog.description
    if (blog.content) {
      // For content, we still need to truncate but preserve HTML structure
      const textContent = stripHtml(blog.content)
      if (textContent.length > 150) {
        // Try to find a good breaking point
        const truncated = textContent.substring(0, 150)
        const lastSpace = truncated.lastIndexOf(' ')
        const finalText =
          lastSpace > 100 ? truncated.substring(0, lastSpace) : truncated
        return finalText + '...'
      }
      return blog.content
    }
    return 'No preview available.'
  }

  const BlogCardSkeleton = () => (
    <Grid item xs={12} sm={6} lg={4}>
      <Card sx={{ height: '100%' }}>
        <Skeleton variant='rectangular' height={220} />
        <CardContent>
          <Skeleton variant='text' height={32} />
          <Skeleton variant='text' height={20} width='60%' />
          <Box sx={{ mt: 2 }}>
            <Skeleton variant='text' height={16} />
            <Skeleton variant='text' height={16} />
            <Skeleton variant='text' height={16} width='80%' />
          </Box>
          <Box
            sx={{
              mt: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Skeleton variant='rounded' width={80} height={24} />
            <Skeleton variant='rounded' width={100} height={32} />
          </Box>
        </CardContent>
      </Card>
    </Grid>
  )
const schemaMarkup = generateBlogListingSchema(blogs)

  return (
    <ThemeProvider theme={theme}>
      <>
      <StaticPageWrapper useDomainSEO
          // title='Informative Tax Insights & Updates | GCC Tax Laws Blogs'
          // description='Stay informed with the latest tax developments, insights, and analysis from across the GCC region. Expert commentary on regulatory changes, compliance updates, and strategic tax planning for businesses operating in the Middle East.'
          // canonical='https://gcctaxlaws.com/blogs'
          // keywords='GCC tax blog, UAE tax insights, Saudi Arabia tax updates, Qatar tax news, Kuwait tax regulations, Bahrain tax compliance, Oman tax law, Middle East tax analysis, corporate tax updates, VAT insights, excise tax news, transfer pricing, tax treaties, FTA decisions, tax compliance guide'
          schemaMarkup={schemaMarkup}
        />
        <Box>
          {/* Hero Section */}
          <HeroSection>
            <Container maxWidth='lg' sx={{ px: { xs: 2, sm: 3 } }}>
              <Box
                sx={{
                  textAlign: 'center',
                  maxWidth: { xs: '100%', sm: '600px', md: '800px' },
                  mx: 'auto',
                  px: { xs: 1, sm: 0 },
                }}
              >
                <Box
                  sx={{
                    display: 'inline-flex',
                    p: { xs: 1.5, sm: 2 },
                    borderRadius: '50%',
                    backgroundColor: 'rgba(255, 218, 27, 0.15)',
                    mb: { xs: 2, sm: 3 },
                  }}
                >
                  <Article
                    sx={{
                      fontSize: { xs: 24, sm: 28, md: 32 },
                      color: '#ffda1b',
                    }}
                  />
                </Box>

                <Typography
                  variant='h1'
                  sx={{ mb: { xs: 1.5, sm: 2 }, px: { xs: 1, sm: 0 } }}
                >
                  ClariTax
                </Typography>
                <GoldDivider />

                <Typography
                  variant='body1'
                  sx={{
                    color: '#e2e8f0',
                    fontSize: { xs: '1rem', sm: '1.0625rem', md: '1.125rem' },
                    mb: { xs: 1.5, sm: 2 },
                    px: { xs: 1, sm: 0 },
                  }}
                >
                  Informative tax blogs by {getCountryFromDomain()}TaxLaws.com
                </Typography>

                <Typography
                  variant='body2'
                  sx={{
                    color: '#cbd5e1',
                    maxWidth: { xs: '100%', sm: '500px', md: '600px' },
                    mx: 'auto',
                    fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                    lineHeight: { xs: 1.5, md: 1.6 },
                    mb: { xs: 1, sm: 1.5 },
                    px: { xs: 1, sm: 0 },
                  }}
                >
                  From treaty interpretation to compliance checklists, we break
                  down complex tax matters with clarity and context. Our content
                  is curated for professionals, entrepreneurs, students, and
                  policymakers alike. Every article is backed by trusted
                  sources, expert research, and regional relevance.
                </Typography>
                <Typography
                  variant='body2'
                  sx={{
                    color: '#cbd5e1',
                    maxWidth: { xs: '100%', sm: '500px', md: '600px' },
                    mx: 'auto',
                    fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                    px: { xs: 1, sm: 0 },
                  }}
                >
                  Whether you're looking for direction or clarity, start here.
                </Typography>
                <Box
                  sx={{
                    mt: { xs: 2.5, sm: 3 },
                    display: 'flex',
                    gap: 1.5,
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                  }}
                >
                  <Button
                    variant='contained'
                    onClick={handleStartWriting}
                    sx={{
                      backgroundColor: '#ffcf51',
                      color: '#232536',
                      textTransform: 'none',
                      fontWeight: 700,
                      '&:hover': {
                        backgroundColor: '#f2be35',
                      },
                    }}
                  >
                    Write a Blog
                  </Button>
                  {!isAuthenticated && (
                    <Button
                      variant='outlined'
                      onClick={() => navigate('/login')}
                      sx={{
                        borderColor: '#ffcf51',
                        color: '#ffcf51',
                        textTransform: 'none',
                        fontWeight: 600,
                      }}
                    >
                      Login to Read Full Blogs
                    </Button>
                  )}
                </Box>
              </Box>
            </Container>
          </HeroSection>

          {/* Search and Filter Section */}
          <FilterSection>
            <Container maxWidth='lg' sx={{ px: { xs: 2, sm: 3 } }}>
              <Tabs
                value={selectedSection}
                onChange={handleSectionChange}
                sx={{
                  mb: 2,
                  '& .MuiTab-root.Mui-selected': {
                    color: '#232536',
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#ffcf51',
                  },
                }}
              >
                {/* <Tab label='All Blogs' value='ALL' /> */}
                <Tab label='Team GTL' value='TEAM_GTL' />
                <Tab label='Community Blogs' value='COMMUNITY' />
              </Tabs>
              <Grid container spacing={{ xs: 2, md: 3 }} alignItems='center'>
                <Grid item xs={12} md={6}>
                  <Box component='form' onSubmit={handleSearch}>
                    <TextField
                      fullWidth
                      placeholder='Search articles...'
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      size={
                        useMediaQuery(theme.breakpoints.down('sm'))
                          ? 'small'
                          : 'medium'
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position='start'>
                            <Search sx={{ fontSize: { xs: 20, sm: 24 } }} />
                          </InputAdornment>
                        ),
                        endAdornment: searchQuery && (
                          <InputAdornment position='end'>
                            <IconButton
                              onClick={() => {
                                setSearchQuery('')
                                if (searchQuery) {
                                  clearFilters()
                                }
                              }}
                              size='small'
                              type='button'
                            >
                              <Clear sx={{ fontSize: { xs: 18, sm: 20 } }} />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: { xs: 1.5, md: 2 },
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          '&:hover fieldset': {
                            borderColor: '#232536',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#232536',
                          },
                        },
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: { xs: 0.5, sm: 1 },
                      justifyContent: { xs: 'flex-start', md: 'flex-end' },
                    }}
                  >
                    <CategoryChip
                      label='All'
                      selected={!selectedCategory}
                      onClick={clearFilters}
                    />
                    {categories?.map((category) => (
                      <CategoryChip
                        key={category}
                        label={category}
                        selected={selectedCategory === category}
                        onClick={() => handleCategoryFilter(category)}
                      />
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </Container>
          </FilterSection>

          {/* Error Alert */}
          {error && (
            <Container maxWidth='lg' sx={{ pt: 2 }}>
              <Alert
                severity='error'
                onClose={() => setError(null)}
                action={
                  <Button color='inherit' size='small' onClick={clearFilters}>
                    Retry
                  </Button>
                }
              >
                {error}
              </Alert>
            </Container>
          )}

          {/* Blogs Section */}
          <Box sx={{ py: 6, backgroundColor: 'white', minHeight: '60vh' }}>
            <Container maxWidth='lg'>
              {loading ? (
                <Grid container spacing={3}>
                  {[...Array(6)].map((_, index) => (
                    <BlogCardSkeleton key={index} />
                  ))}
                </Grid>
              ) : (
                <>
                  <Grid container spacing={{ xs: 2, sm: 3 }}>
                    {blogs?.map((blog) => {
                      const authorProfilePath = getAuthorPublicProfilePath(blog)
                      return (
                      <Grid item xs={12} sm={6} lg={4} key={blog.id} sx={{ display: 'flex' }}>
                        <BlogCard onClick={() => handleBlogClick(blog.slug)}>
                          <Box
                            sx={{
                              width: '100%',
                              aspectRatio: '16 / 9',
                              overflow: 'hidden',
                              backgroundColor: '#f3f4f6',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <CardMedia
                              className='blog-image'
                              component='img'
                              image={blog.imageUrl || '/favicon.svg'}
                              alt={stripHtml(blog.title)}
                              sx={{
                                width: '100%',
                                height: '100%',
                                display: 'block',
                                objectFit: 'cover',
                                objectPosition: 'center',
                                transition:
                                  'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                  transform: { xs: 'none', sm: 'scale(1.05)' },
                                },
                              }}
                            />
                          </Box>
                          <CardContent
                            sx={{
                              p: { xs: 2, sm: 2.5, md: 3 },
                              '&:last-child': { pb: { xs: 2, sm: 2.5, md: 3 } },
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              flexGrow: 1,
                            }}
                          >
                            {/* Top Content Container */}
                            <Box sx={{ flexGrow: 1 }}>
                              {/* Category Badges */}
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  mb: { xs: 1.5, sm: 2 },
                                  gap: { xs: 0.5, sm: 1 },
                                  flexWrap: 'wrap',
                                  minHeight: { xs: 20, sm: 24 },
                                }}
                              >
                                <Chip
                                  label={blog.category}
                                  size='small'
                                  sx={{
                                    backgroundColor: 'rgba(255, 218, 27, 0.1)',
                                    color: '#23252a',
                                    fontWeight: 600,
                                    fontSize: {
                                      xs: '0.6875rem',
                                      sm: '0.75rem',
                                    },
                                    height: { xs: 20, sm: 24 },
                                  }}
                                />
                                {/* <Chip
                                  label={
                                    isCommunityAuthor(blog.authorType)
                                      ? 'Community Blog'
                                      : 'Team GTL'
                                  }
                                  size='small'
                                  sx={{
                                    backgroundColor:
                                      isCommunityAuthor(blog.authorType)
                                        ? 'rgba(34, 197, 94, 0.12)'
                                        : 'rgba(35, 37, 54, 0.12)',
                                    color:
                                      isCommunityAuthor(blog.authorType)
                                        ? '#166534'
                                        : '#232536',
                                    fontWeight: 600,
                                    fontSize: {
                                      xs: '0.6875rem',
                                      sm: '0.75rem',
                                    },
                                    height: { xs: 20, sm: 24 },
                                  }}
                                /> */}
                                {blog.betaVersion && (
                                  <Chip
                                    label='Beta'
                                    size='small'
                                    sx={{
                                      backgroundColor:
                                        'rgba(59, 130, 246, 0.1)',
                                      color: '#3b82f6',
                                      fontWeight: 600,
                                      fontSize: {
                                        xs: '0.6875rem',
                                        sm: '0.75rem',
                                      },
                                      height: { xs: 20, sm: 24 },
                                    }}
                                  />
                                )}
                              </Box>

                              <Typography
                                component='h3'
                                className='blog-title'
                                sx={{
                                  color: 'text.primary',
                                  mb: { xs: 1, sm: 1.5 },
                                  fontSize: {
                                    xs: '1rem',
                                    sm: '1.0625rem',
                                    md: '1.1rem',
                                  },
                                  lineHeight: { xs: 1.3, md: 1.4 },
                                  fontWeight: 600,
                                  fontFamily: 'Montserrat, sans-serif',
                                  transition: 'color 0.3s ease',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  minHeight: { xs: '2.6em', sm: '2.8em' },
                                }}
                              >
                                {stripHtml(blog.title) || 'Untitled'}
                              </Typography>
                              <Box
                                component='div'
                                sx={{
                                  mb: { xs: 1.5, sm: 2 },
                                  display: '-webkit-box',
                                  WebkitLineClamp: { xs: 2, sm: 3 },
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  lineHeight: 1.5,
                                  minHeight: { xs: '3em', sm: '4.5em' },
                                  fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                                  fontFamily: 'Montserrat, sans-serif',
                                  color: 'text.secondary',

                                  // HTML content styling
                                  '& *': {
                                    fontSize: 'inherit !important',
                                    lineHeight: 'inherit !important',
                                    color: 'inherit !important',
                                    fontWeight: 'inherit !important',
                                    fontFamily:
                                      'Montserrat, sans-serif !important',
                                  },
                                  '& p': {
                                    margin: '0 0 0.5em 0',
                                    display: 'inline',
                                    fontFamily:
                                      'Montserrat, sans-serif !important',
                                    '&:last-child': {
                                      marginBottom: 0,
                                    },
                                  },
                                  '& strong, & b': {
                                    fontWeight: '600 !important',
                                    fontFamily:
                                      'Montserrat, sans-serif !important',
                                  },
                                  '& em, & i': {
                                    fontStyle: 'italic !important',
                                    fontFamily:
                                      'Montserrat, sans-serif !important',
                                  },
                                  '& a': {
                                    color: 'primary.main !important',
                                    textDecoration: 'underline',
                                    fontFamily:
                                      'Montserrat, sans-serif !important',
                                    // '&:hover': {
                                    //   textDecoration: 'none',
                                    // },
                                  },
                                  // Handle inline elements for text truncation
                                  '& div, & span': {
                                    display: 'inline',
                                    fontFamily:
                                      'Montserrat, sans-serif !important',
                                  },
                                  // Remove margins and convert block elements to inline
                                  '& h1, & h2, & h3, & h4, & h5, & h6': {
                                    display: 'inline',
                                    fontSize: 'inherit !important',
                                    fontWeight: '600 !important',
                                    fontFamily:
                                      'Montserrat, sans-serif !important',
                                    margin: 0,
                                  },
                                  '& ul, & ol': {
                                    display: 'inline',
                                    padding: 0,
                                    listStyle: 'none',
                                    fontFamily:
                                      'Montserrat, sans-serif !important',
                                    margin: 0,
                                  },
                                  '& li': {
                                    display: 'inline',
                                    fontFamily:
                                      'Montserrat, sans-serif !important',
                                    '&:not(:last-child)::after': {
                                      content: '", "',
                                    },
                                  },
                                  // Hide images in excerpts to save space
                                  '& img': {
                                    display: 'none',
                                  },
                                }}
                              >
                                {parseHtmlSanitizeAddTargetToLinks(
                                  generateExcerpt(blog),
                                  null, // searchQuery
                                  `/blogs/${blog.slug}`,
                                  {
                                    fullDocumentContent: generateExcerpt(blog),
                                    documentTitle:
                                      stripHtml(blog.title) || 'Blog Post',
                                    showSnackbar: null, // No snackbar in listing page
                                  }
                                )}
                              </Box>

                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  // mb: { xs: 1.5, sm: 2 },
                                  gap: { xs: 1, sm: 1.5, md: 2 },
                                  flexWrap: 'wrap',
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                    minWidth: 'fit-content',
                                    cursor: authorProfilePath ? 'pointer' : 'default',
                                    px: authorProfilePath ? 0.75 : 0,
                                    py: authorProfilePath ? 0.35 : 0,
                                    borderRadius: 1,
                                    transition: 'background-color 0.2s ease',
                                    ...(authorProfilePath
                                      ? {
                                          '&:hover': {
                                            backgroundColor: alpha('#ffcf51', 0.2),
                                          },
                                          '&:focus-visible': {
                                            outline: `2px solid ${alpha('#ffcf51', 0.45)}`,
                                            outlineOffset: 1,
                                            backgroundColor: alpha('#ffcf51', 0.2),
                                          },
                                        }
                                      : {}),
                                  }}
                                  onClick={(event) => {
                                    if (!authorProfilePath) {
                                      return
                                    }
                                    event.stopPropagation()
                                    navigate(authorProfilePath)
                                  }}
                                  onKeyDown={(event) => {
                                    if (!authorProfilePath) {
                                      return
                                    }
                                    if (event.key !== 'Enter' && event.key !== ' ') {
                                      return
                                    }
                                    event.preventDefault()
                                    event.stopPropagation()
                                    navigate(authorProfilePath)
                                  }}
                                  role={authorProfilePath ? 'link' : undefined}
                                  tabIndex={authorProfilePath ? 0 : undefined}
                                  aria-label={
                                    authorProfilePath
                                      ? `Open ${getAuthorDisplayName(blog)} profile`
                                      : undefined
                                  }
                                >
                                  <Avatar
                                    src={getAuthorAvatarSource(blog) || undefined}
                                    alt={getAuthorDisplayName(blog)}
                                    imgProps={{ referrerPolicy: 'no-referrer' }}
                                    sx={{
                                      width: { xs: 20, sm: 22 },
                                      height: { xs: 20, sm: 22 },
                                      fontSize: { xs: '0.58rem', sm: '0.62rem' },
                                      bgcolor: isCommunityAuthor(blog.authorType) ? '#e2e8f0' : '#1f2a44',
                                      color: isCommunityAuthor(blog.authorType) ? '#334155' : '#ffffff',
                                      cursor: authorProfilePath ? 'pointer' : 'default',
                                    }}
                                  >
                                    {getInitials(getAuthorDisplayName(blog))}
                                  </Avatar>
                                  <Typography
                                    variant='caption'
                                    color='text.secondary'
                                    sx={{
                                      fontSize: {
                                        xs: '0.6875rem',
                                        sm: '0.75rem',
                                      },
                                      lineHeight: 1.2,
                                      display: 'flex',
                                      alignItems: 'center',
                                      cursor: authorProfilePath ? 'pointer' : 'default',
                                    }}
                                  >
                                    {getAuthorDisplayName(blog)}
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    minWidth: 'fit-content',
                                    py: 0.35,
                                  }}
                                >
                                  <CalendarToday
                                    sx={{
                                      fontSize: { xs: 12, sm: 14 },
                                      color: 'text.secondary',
                                    }}
                                  />
                                  <Typography
                                    variant='caption'
                                    color='text.secondary'
                                    sx={{
                                      fontSize: {
                                        xs: '0.6875rem',
                                        sm: '0.75rem',
                                      },
                                      lineHeight: 1.2,
                                      display: 'flex',
                                      alignItems: 'center',
                                    }}
                                  >
                                    {formatDate(blog.publishedDate)}
                                  </Typography>
                                </Box>
                                {blog.readingTime && (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.5,
                                    }}
                                  >
                                    <AccessTime
                                      sx={{
                                        fontSize: 14,
                                        color: 'text.secondary',
                                      }}
                                    />
                                    <Typography
                                      variant='caption'
                                      color='text.secondary'
                                      sx={{ fontSize: '0.75rem' }}
                                    >
                                      {blog.readingTime} min read
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            </Box>

                            <Box
                              sx={{
                                mt: { xs: 2, sm: 2.5 },

                                alignSelf: 'flex-start',
                              }}
                            >
                              <Button
                                size='small'
                                endIcon={
                                  <ArrowForward
                                    sx={{ fontSize: { xs: 14, sm: 16 } }}
                                  />
                                }
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleBlogClick(blog.slug)
                                }}
                                sx={{
                                  padding: { xs: '6px 16px', sm: '8px 20px' },
                                  borderRadius: { xs: '4px', sm: '6px' },
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                                  backgroundColor: '#ffda1b',
                                  color: '#23252a',
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    backgroundColor: '#f5c842',
                                    transform: {
                                      xs: 'none',
                                      sm: 'translateY(-1px)',
                                    },
                                  },
                                }}
                              >
                                {'Read More'}
                              </Button>
                            </Box>
                          </CardContent>
                        </BlogCard>
                      </Grid>
                      )
                    })}
                  </Grid>

                  {/* Load More Button */}
                  {hasMore && !loading && (
                    <Box
                      sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}
                    >
                      <Button
                        onClick={handleLoadMore}
                        disabled={loadingMore}
                        startIcon={
                          loadingMore ? (
                            <CircularProgress size={20} color='inherit' />
                          ) : null
                        }
                        sx={{
                          padding: { xs: '12px 24px', sm: '14px 28px' },
                          borderRadius: { xs: '6px', md: '8px' },
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          backgroundColor: '#23252a',
                          color: 'white',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: '#1a1c21',
                            transform: { xs: 'none', sm: 'translateY(-1px)' },
                            boxShadow: {
                              xs: 'none',
                              sm: '0 4px 12px rgba(35, 37, 42, 0.3)',
                            },
                          },
                          '&:disabled': {
                            backgroundColor: '#9ca3af',
                            transform: 'none',
                            boxShadow: 'none',
                          },
                        }}
                      >
                        {loadingMore ? 'Loading...' : 'Load More Articles'}
                      </Button>
                    </Box>
                  )}

                  {/* No Results */}
                  {!loading && blogs?.length === 0 && !error && (
                    <Box
                      sx={{
                        textAlign: 'center',
                        py: { xs: 6, sm: 8 },
                        px: { xs: 2, sm: 0 },
                      }}
                    >
                      <Article
                        sx={{
                          fontSize: { xs: 48, sm: 56, md: 64 },
                          color: 'text.secondary',
                          mb: { xs: 1.5, sm: 2 },
                        }}
                      />
                      <Typography
                        variant='h4'
                        color='text.secondary'
                        sx={{
                          mb: { xs: 0.5, sm: 1 },
                          fontSize: {
                            xs: '1.25rem',
                            sm: '1.5rem',
                            md: '1.75rem',
                          },
                        }}
                      >
                        No articles found
                      </Typography>
                      <Typography
                        variant='body1'
                        color='text.secondary'
                        sx={{
                          mb: { xs: 2, sm: 3 },
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          maxWidth: '400px',
                          mx: 'auto',
                        }}
                      >
                        {searchQuery || selectedCategory
                          ? 'Try adjusting your search terms or filters'
                          : 'No articles are currently available'}
                      </Typography>
                      {(searchQuery || selectedCategory) && (
                        <Button
                          variant='outlined'
                          onClick={clearFilters}
                          sx={{
                            borderColor: '#232536',
                            color: '#232536',
                            textTransform: 'none',
                            '&:hover': {
                              borderColor: '#3a3e5a',
                              color: '#3a3e5a',
                            },
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                            padding: { xs: '8px 16px', sm: '10px 20px' },
                          }}
                        >
                          Clear Filters
                        </Button>
                      )}
                    </Box>
                  )}
                </>
              )}
            </Container>
          </Box>
        </Box>
        <Snackbar
          open={writerNoticeOpen}
          autoHideDuration={3500}
          onClose={() => setWriterNoticeOpen(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            severity='success'
            onClose={() => setWriterNoticeOpen(false)}
            sx={{ width: '100%' }}
          >
            {writerNotice}
          </Alert>
        </Snackbar>
      </>
    </ThemeProvider>
  )
}
export default Blogs
